import {Button, Chip, Radio} from '@jobseeker/lotus/elements';
import Loader from '@jobseeker/lotus/components/Loader';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {
  Box,
  Typography,
  TextField as BaseTextField,
  styled,
  RadioGroup,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  SurveyCheck as SurveyCheckYellow,
  ThumbsUpYellow,
  ThumbsDownGrey,
  GreenBag,
  ThumbsUpGreen,
  SurveyCheckGreen,
  CrossOutlined,
} from '../../assets/icons';
import {useEffect, useState} from 'react';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {parseString} from './utils';

import type {SurveyQuestionType, SurveyAnswerType} from './types';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {useInterceptor} from '@jobseeker/common-core';
import {colorPalette} from '@jobseeker/lotus/foundations/colors';

const TextField = styled(BaseTextField)(({theme}) => ({
  'textarea::placeholder': {
    fontStyle: 'normal',
  },
  '.MuiInputBase-root': {
    fontSize: theme.breakpoints.up('sm') ? 12 : 10,
    lineHeight: '15px',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    with: '100%',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
}));

const SurveyQuestion = ({
  currQuestion,
  questionsMapping,
  setCurrentQuestion,
  setShowSurvey,
  primaryColor,
  surveyTextColor,
  submitAnswer,
  surveyType,
}: SurveyQuestionType) => {
  const metadata: any = useInterceptor();
  const {question, cta, jumpTo, type, subType, options} = currQuestion;
  const ctaTexts = JSON.parse(cta);
  const jumpToMapping = JSON.parse(jumpTo);
  const optionsMapping = JSON.parse(options);
  const subTypes = JSON.parse(subType);
  const [selectedChips, setSelectedChips] = useState([]);
  const [selectedRadioOption, setSelectedRadioOption] = useState('');
  const [textValue, setTextValue] = useState('');
  const [showTextError, setShowTextError] = useState(false);
  const [loading, setLoading] = useState(false);
  const isLargeDevice = useIsLargeDevice();
  const theme = useWrappedTheme();

  useEffect(() => {
    const isRelevantJob = surveyType === 'RelevantJob';
    const textLength = textValue?.length || 0;
    const showError = isRelevantJob
      ? (textLength > 0 && textLength < 10) || textLength > 200
      : textLength > 250;
    setShowTextError(showError);
  }, [textValue, surveyType]);

  const handleCTAClick = async (target: string) => {
    setLoading(true);
    const parsedCta = parseString(target, false) as string;

    if (surveyType === 'Marketing') {
      TrackEventUBA('surveyClick', 'click', {
        pageName: metadata.page || 'unknown',
        sessionUsername: sessionStorage.getItem('userId') || '',
        surveyName: 'Marketing PR Survey MBA Cohort',
        surveyId: currQuestion.surveyId,
        clickType: parsedCta,
        questionNumber: currQuestion.id,
      });
    }

    const answer: {[key: string]: string} = {
      buttons: parsedCta,
    };
    if (selectedChips?.length) {
      answer['chips'] = JSON.stringify(selectedChips);
    }
    if (textValue?.length) {
      answer['text'] = textValue;
    }

    if (selectedRadioOption?.length) {
      answer['radio'] = selectedRadioOption;
    }

    const answerResponse: SurveyAnswerType = await submitAnswer(
      JSON.stringify(answer)
    );

    if (answerResponse?.id) {
      setLoading(false);

      if (jumpToMapping[parsedCta]) {
        setCurrentQuestion(questionsMapping[jumpToMapping[parsedCta]]);
      } else {
        setShowSurvey(false);
      }

      setSelectedChips([]);
      setTextValue('');
      setSelectedRadioOption('');
    }
  };

  const replaceIconsFromText = (
    text: string,
    fontSize: string,
    fontWeight: number,
    fontFamily: string,
    lineHeight: string,
    mt: string
  ) => {
    const textComponent = (src: string) => {
      const source =
        src === '{ThumbsUpYellow}'
          ? ThumbsUpYellow
          : src === '{ThumbsDownGrey}'
            ? ThumbsDownGrey
            : src === '{SurveyCheckYellow}'
              ? SurveyCheckYellow
              : src === '{GreenBag}'
                ? GreenBag
                : src === '{ThumbsUpGreen}'
                  ? ThumbsUpGreen
                  : src === '{SurveyCheckGreen}'
                    ? SurveyCheckGreen
                    : null;

      if (source) {
        if (src === '{SurveyCheckYellow}') {
          return <Image src={source} width={48} height={48} alt="" />;
        } else if (src === '{SurveyCheckGreen}') {
          return <Image src={source} width={28} height={28} alt="" />;
        } else if (src === '{ThumbsUpGreen}') {
          return (
            <Image
              src={source}
              width={22}
              height={22}
              alt=""
              style={{marginBottom: '5px'}}
            />
          );
        } else if (src === '{ThumbsDownGrey}' && surveyType === 'RelevantJob') {
          return (
            <Image
              src={source}
              width={22}
              height={22}
              alt=""
              style={{marginTop: '5px'}}
            />
          );
        } else {
          return <Image src={source} width={22} height={22} alt="" />;
        }
      } else {
        return (
          <Typography
            fontFamily={fontFamily}
            lineHeight={lineHeight}
            fontSize={fontSize}
            fontWeight={fontWeight}
            mt={mt}
            component={'span'}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: src.replace(/\\n/g, '<br/>') as string,
              }}
            />
          </Typography>
        );
      }
    };

    const replacementText = parseString(text, true);

    return (
      <Typography
        component={'span'}
        display={'flex'}
        alignItems={'center'}
        gap={1}
        fontFamily={fontFamily}
        lineHeight={lineHeight}
        fontSize={fontSize}
        fontWeight={fontWeight}
      >
        {replacementText.map((text: string) => {
          if (text?.length) {
            return textComponent(text);
          }
        })}
      </Typography>
    );
  };
  return (
    <Box
      width={
        surveyType === 'RelevantJob' ? '100%' : isLargeDevice ? '70%' : '100%'
      }
      height={'100%'}
      display={'flex'}
      flexDirection={
        surveyType === 'RelevantJob' && type === 'singleSelect'
          ? 'row'
          : 'column'
      }
      gap={1.5}
      justifyContent={'space-between'}
      alignItems={
        surveyType === 'RelevantJob' && type === 'singleSelect'
          ? 'center'
          : 'flex-start'
      }
      flexWrap={'wrap'}
      sx={{
        zIndex: 2,
      }}
    >
      {type === 'noSelect' ? (
        <Typography
          component={'span'}
          lineHeight={surveyType === 'RelevantJob' ? '16px' : '30px'}
          fontSize={
            surveyType === 'RelevantJob'
              ? theme.typography.pxToRem(12)
              : isLargeDevice
                ? theme.typography.pxToRem(24)
                : theme.typography.pxToRem(18)
          }
          fontWeight={surveyType === 'RelevantJob' ? 500 : 700}
          fontFamily={surveyType === 'RelevantJob' ? 'Poppins' : 'Abhaya Libre'}
          color={
            surveyType === 'Marketing'
              ? colorPalette.prompt['A200']
              : surveyTextColor
          }
        >
          {replaceIconsFromText(
            question,
            surveyType === 'RelevantJob'
              ? theme.typography.pxToRem(12)
              : isLargeDevice
                ? theme.typography.pxToRem(24)
                : theme.typography.pxToRem(18),
            surveyType === 'RelevantJob' ? 500 : 700,
            surveyType === 'RelevantJob' ? 'Poppins' : 'Abhaya Libre',
            surveyType === 'RelevantJob' ? '16px' : '30px',
            '0px'
          )}
          {surveyType === 'Marketing' && (
            <Box
              display={'flex'}
              position="absolute"
              right={23}
              top="50%"
              sx={{
                transform: 'translateY(-50%)',
                cursor: 'pointer',
              }}
              onClick={() => setShowSurvey(false)}
            >
              <Image
                src={CrossOutlined}
                width={18}
                height={18}
                alt="Cross Icon"
              />
            </Box>
          )}
        </Typography>
      ) : (
        <Typography component={'span'}>
          {replaceIconsFromText(
            question,
            surveyType === 'RelevantJob'
              ? theme.typography.pxToRem(14)
              : isLargeDevice
                ? theme.typography.pxToRem(14)
                : theme.typography.pxToRem(12),
            500,
            'Poppins',
            isLargeDevice ? '20px' : '16px',
            '3px'
          )}
        </Typography>
      )}
      {subTypes.map((subtype: string) => {
        if (subtype === 'chips') {
          return (
            <Chip
              versionVariant={VersionVariants.VERSION_TWO}
              selectedChips={selectedChips}
              setSelectedChips={setSelectedChips}
              chipPrimaryColor={primaryColor}
              chipsData={optionsMapping['chips']}
            />
          );
        } else if (
          subtype === 'text' &&
          (selectedChips.length || selectedRadioOption === 'Others')
        ) {
          return (
            <Box
              display="flex"
              flexDirection="column"
              gap={0.75}
              alignItems="flex-end"
              width="100%"
            >
              <TextField
                sx={{
                  width: '100%',
                  borderRadius: 1.75,
                  // border: showTextError
                  //   ? '1px solid #D11124'
                  //   : '1px solid #ffffff',
                  background: '#ffffff',
                  fontSize: isLargeDevice
                    ? theme.typography.pxToRem(12)
                    : theme.typography.pxToRem(10),
                  fontWeight: 400,
                  lineHeight: '15px',
                  '& fieldset': {border: '1px solid transparent'},
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: showTextError ? '#D11124' : 'secondary.dark',
                    },
                  },
                }}
                multiline
                maxRows={5}
                rows={5}
                inputProps={{}}
                placeholder={optionsMapping['text']}
                value={textValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setTextValue(event?.target?.value);
                }}
              />
              <Typography
                variant="caption"
                lineHeight="15px"
                color={
                  surveyType === 'RelevantJob' ? 'text.secondary' : '#D11124'
                }
                display={showTextError ? 'flex' : 'none'}
              >
                {surveyType === 'RelevantJob'
                  ? 'min 10 and max 200 characters'
                  : 'Max 250 characters'}
              </Typography>
            </Box>
          );
        } else if (subtype === 'buttons') {
          return (
            <Box
              display={optionsMapping['buttons']?.length ? 'flex' : 'none'}
              justifyContent="flex-start"
              alignItems="center"
              gap={0.75}
            >
              {optionsMapping['buttons'].map((buttonText: string) => (
                <Button
                  disabled={
                    loading
                      ? true
                      : buttonText === 'Cancel'
                        ? false
                        : showTextError
                          ? true
                          : false
                  }
                  variant="outlined"
                  onClick={() => {
                    handleCTAClick(buttonText);
                  }}
                  sx={{
                    minWidth: surveyType === 'RelevantJob' ? '72px' : '130px',
                    paddingX: 2,
                    paddingY: 0.5,
                    border: buttonText === 'Cancel' ? `0px` : `1px solid`,
                    borderColor: `${surveyType === 'Marketing' ? colorPalette.prompt['A100'] : primaryColor}`,
                    borderRadius: 8,
                    background: '#ffffff',
                    color: 'secondary.main',
                    fontSize: theme.typography.pxToRem(12),
                    fontWeight: 500,
                    lineHeight: '24px',
                    ':hover': {
                      background: '#ffffff',
                      borderColor: `${surveyType === 'Marketing' ? colorPalette.prompt['A100'] : primaryColor}`,
                      boxShadow:
                        buttonText === 'Cancel'
                          ? '0px 0px 0px 0px #29292914'
                          : '0px 4px 4px 0px #29292914',
                    },
                    ':disabled': {
                      color:
                        buttonText === 'Cancel' ? 'secondary.main' : '#C0C1C2',
                      borderColor:
                        buttonText === 'Cancel' ? 'transparent' : '#E0E0E0',
                      background:
                        buttonText === 'Cancel' ? 'transparent' : '#E0E0E0',
                    },
                  }}
                >
                  {!loading ? (
                    replaceIconsFromText(
                      buttonText,
                      theme.typography.pxToRem(12),
                      500,
                      'Poppins',
                      '24px',
                      '0px'
                    )
                  ) : (
                    <Loader size={20} sx={{color: primaryColor}} />
                  )}
                </Button>
              ))}
            </Box>
          );
        } else if (subtype === 'radio') {
          return (
            <Box
              display={optionsMapping['radio']?.length ? 'flex' : 'none'}
              justifyContent="flex-start"
              alignItems="center"
              gap={0.75}
            >
              <RadioGroup
                sx={{
                  ml: 0.25,
                  flexDirection: 'row',
                  gap: 1.25,
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedRadioOption(event.target.value as string);
                }}
              >
                {optionsMapping['radio'].map((button: any, index: number) => (
                  <Box
                    paddingRight={1.5}
                    borderRadius={1.5}
                    bgcolor="#ffffff"
                    display="inline-flex"
                    height="26px"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                    flexShrink={0}
                  >
                    <Radio
                      key={`options-${button}-${index}`}
                      size={12}
                      name="handleTeam"
                      value={button}
                      labelPlacement="start"
                      variant="survey"
                      sx={{
                        cursor: 'pointer',
                      }}
                      label={
                        <Typography variant="subtitle3" lineHeight="18px">
                          {button}
                        </Typography>
                      }
                    />
                  </Box>
                ))}
              </RadioGroup>
            </Box>
          );
        }
      })}
      <Box
        display={ctaTexts?.length ? 'flex' : 'none'}
        justifyContent={'flex-start'}
        alignItems={'center'}
        gap={0.75}
      >
        {ctaTexts.map((ctaText: string) => {
          return (
            <Button
              disabled={
                loading
                  ? true
                  : ctaText === 'Cancel'
                    ? false
                    : showTextError
                      ? true
                      : subType.includes('chips')
                        ? selectedChips.length
                          ? false
                          : true
                        : subType.includes('radio')
                          ? selectedRadioOption.length
                            ? false
                            : true
                          : false
              }
              variant="outlined"
              onClick={() => {
                handleCTAClick(ctaText);
              }}
              sx={{
                minWidth: '78px',
                paddingX: 2,
                paddingY: 0.5,
                border: ctaText === 'Cancel' ? '0px' : `1px solid`,
                marginLeft: ctaText === 'Cancel' ? '-10px' : `0px`,
                borderColor:
                  ctaText === 'Cancel'
                    ? 'transparent'
                    : `${surveyType === 'Marketing' ? colorPalette.prompt['A100'] : primaryColor}`,
                borderRadius: 8,
                background: ctaText === 'Cancel' ? 'transparent' : '#ffffff',
                color: 'secondary.main',
                fontSize: theme.typography.pxToRem(12),
                fontWeight: 500,
                lineHeight: '24px',
                ':hover': {
                  background: ctaText === 'Cancel' ? 'transparent' : '#ffffff',
                  border: ctaText === 'Cancel' ? '0px' : `1px solid`,
                  borderColor:
                    ctaText === 'Cancel'
                      ? 'transparent'
                      : `${surveyType === 'Marketing' ? colorPalette.prompt['A100'] : primaryColor}`,
                  boxShadow:
                    ctaText === 'Cancel'
                      ? '0px 0px 0px 0px #29292914'
                      : '0px 4px 4px 0px #29292914',
                },
                ':disabled': {
                  color:
                    ctaText === 'Cancel'
                      ? 'secondary.main'
                      : 'rgba(0, 0, 0, 0.26)',
                  borderColor:
                    ctaText === 'Cancel' || surveyType === 'Marketing'
                      ? 'transparent'
                      : '#E0E0E0',
                  background:
                    ctaText === 'Cancel'
                      ? 'transparent'
                      : 'rgba(0, 0, 0, 0.16)',
                },
              }}
            >
              {!loading || ctaText === 'Cancel' ? (
                replaceIconsFromText(
                  ctaText,
                  theme.typography.pxToRem(12),
                  500,
                  'Poppins',
                  '24px',
                  '0px'
                )
              ) : (
                <Loader size={20} sx={{color: primaryColor}} />
              )}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default SurveyQuestion;
